import { alpha } from "@mui/material/styles";
import type { FloatingOptions, Options, Styles } from "react-joyride";
import { ALERT_RED, DARK_SURFACES, OFF_WHITE } from "@/shared/theme";

export const productTourFloatingOptions: FloatingOptions = {
  strategy: "fixed",
  autoUpdate: { ancestorScroll: true, ancestorResize: true, elementResize: true, layoutShift: true, animationFrame: true },
  flipOptions: { boundary: [], rootBoundary: "viewport", padding: 16, fallbackAxisSideDirection: "start" },
  shiftOptions: {
    boundary: [],
    rootBoundary: "viewport",
    padding: 16,
    crossAxis: false,
  },
};

export const productTourOptions = (compact: boolean): Partial<Options> => ({
  arrowBase: compact ? 18 : 22,
  arrowColor: ALERT_RED.main,
  arrowSize: compact ? 9 : 11,
  arrowSpacing: 1,
  backgroundColor: OFF_WHITE.main,
  blockTargetInteraction: true,
  buttons: ["back", "skip", "primary"],
  offset: compact ? 12 : 16,
  overlayClickAction: false as const,
  overlayColor: alpha(DARK_SURFACES.canvas, 0.76),
  primaryColor: ALERT_RED.main,
  scrollDuration: 520,
  showProgress: true,
  spotlightPadding: compact ? 5 : 7,
  spotlightRadius: 0,
  textColor: DARK_SURFACES.canvas,
  zIndex: 1600,
});

export const productTourStyles = (
  compact: boolean,
  fontFamily: string,
): Pick<
  Styles,
  | "arrow"
  | "buttonBack"
  | "buttonClose"
  | "buttonPrimary"
  | "buttonSkip"
  | "floater"
  | "spotlight"
  | "tooltip"
  | "tooltipContainer"
  | "tooltipContent"
  | "tooltipFooter"
> => ({
  arrow: {
    color: ALERT_RED.main,
    filter: `drop-shadow(2px 2px 0 ${DARK_SURFACES.canvas})`,
  },
  buttonBack: {
    color: DARK_SURFACES.canvas,
    fontFamily,
    fontSize: ".7rem",
    fontWeight: 800,
    letterSpacing: ".06em",
    minHeight: 40,
    padding: "8px 10px",
    textTransform: "uppercase",
  },
  buttonClose: { color: DARK_SURFACES.canvas },
  buttonPrimary: {
    backgroundColor: ALERT_RED.main,
    border: `2px solid ${DARK_SURFACES.canvas}`,
    borderRadius: 0,
    boxShadow: `3px 3px 0 ${DARK_SURFACES.canvas}`,
    color: DARK_SURFACES.canvas,
    fontFamily,
    fontSize: ".7rem",
    fontWeight: 900,
    letterSpacing: ".07em",
    minHeight: 40,
    padding: "8px 13px",
    textTransform: "uppercase",
  },
  buttonSkip: {
    color: alpha(DARK_SURFACES.canvas, 0.72),
    fontFamily,
    fontSize: ".68rem",
    fontWeight: 800,
    letterSpacing: ".05em",
    minHeight: 40,
    padding: "8px 6px",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  floater: { filter: "none" },
  spotlight: { stroke: ALERT_RED.main, strokeWidth: 2 },
  tooltip: {
    backgroundColor: OFF_WHITE.main,
    border: `2px solid ${DARK_SURFACES.canvas}`,
    borderRadius: 0,
    boxShadow: `${compact ? 5 : 8}px ${compact ? 5 : 8}px 0 ${ALERT_RED.main}`,
    fontFamily,
    display: "flex",
    flexDirection: "column",
    maxWidth: compact ? "calc(100vw - 32px)" : "min(390px, calc(100vw - 32px))",
    maxHeight: "calc(100dvh - 32px)",
    overflowWrap: "anywhere",
    padding: 0,
  },
  tooltipContainer: {
    minHeight: 0,
    overflowY: "auto",
    padding: compact ? "18px 18px 14px" : "22px 22px 16px",
    textAlign: "left",
  },
  tooltipContent: { padding: 0 },
  tooltipFooter: {
    alignItems: "center",
    flexShrink: 0,
    flexWrap: "wrap",
    borderTop: `1px solid ${DARK_SURFACES.canvas}`,
    gap: 8,
    marginTop: 0,
    padding: compact ? "10px 12px 12px" : "12px 16px 16px",
  },
});
